import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useSection, useSiteContent } from "../context/SiteContentContext";
import { Reveal } from "../components/common/Reveal";
import { SectionLabel } from "../components/common/SectionLabel";
import { TechCard } from "../components/common/TechCard";
import SEO from "../components/common/SEO";
import MarkdownBody from "../components/common/MarkdownBody";

function slugify(s = "") {
    return s
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/[\s_-]+/g, "-")
        .slice(0, 80);
}

export default function CaseStudyDetail() {
    const { slug } = useParams();
    const { content } = useSiteContent();
    const CASES = useSection("case_studies", []);
    // Wait for CMS content to load before deciding 404 — avoids redirect race
    if (content == null) {
        return <div className="max-w-3xl mx-auto px-6 md:px-10 py-28 tech-label" data-testid="case-loading">Loading…</div>;
    }
    const idx = CASES.findIndex((c) => (c.slug || slugify(c.title || c.id || "")) === slug);
    if (idx === -1) {
        // No match — send back to index
        return <Navigate to="/case-studies" replace />;
    }
    const c = CASES[idx];
    const next = CASES[(idx + 1) % CASES.length];
    const partners = (c.partners || []).filter(Boolean);
    const outcomes = (c.outcomes || []).filter(Boolean);

    return (
        <>
            <SEO
                title={`${c.title} · Skovomax Case Studies`}
                description={c.body?.slice(0, 200) || c.title}
                image={c.cover_image}
            />
            <section className="pt-24 pb-10 border-b border-white/10 relative overflow-hidden">
                <div className="absolute inset-0 photonic-grid opacity-15 pointer-events-none" />
                <div className="max-w-[1200px] mx-auto px-6 md:px-10 relative">
                    <Link
                        to="/case-studies"
                        className="inline-flex items-center gap-2 tech-label mb-10 hover:text-white"
                        data-testid="case-back"
                    >
                        <ArrowLeft size={14} /> All Case Studies
                    </Link>
                    <Reveal>
                        <SectionLabel label="Case Study / Entry" />
                        <div className="flex flex-wrap items-center gap-3 mb-6">
                            {c.tag && <span className="chip chip-active">{c.tag}</span>}
                            {c.client && (
                                <span className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-white/55">
                                    {c.client}
                                </span>
                            )}
                        </div>
                        <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tight max-w-5xl">
                            {c.title}
                        </h1>
                    </Reveal>
                </div>
            </section>

            <section className="py-16 border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
                    {/* Left column: metric + partners + outcomes rail */}
                    <aside className="md:col-span-3 space-y-10 md:sticky md:top-24 self-start">
                        {c.metric && (
                            <div data-testid="case-metric">
                                <div className="font-serif-display text-6xl md:text-7xl text-[var(--accent-mercury)] leading-none">
                                    {c.metric}
                                </div>
                                <div className="mt-3 font-mono-tech text-[11px] tracking-[0.22em] uppercase text-white/55">
                                    {c.metric_label || c.metricLabel}
                                </div>
                            </div>
                        )}
                        {partners.length > 0 && (
                            <div data-testid="case-partners">
                                <div className="overline mb-3">Partners</div>
                                <ul className="space-y-2 text-white/80 text-[14px]">
                                    {partners.map((p, i) => (
                                        <li key={i} className="border-l border-[var(--accent-indigo)] pl-3 leading-snug">
                                            {p}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {outcomes.length > 0 && (
                            <div data-testid="case-outcomes">
                                <div className="overline mb-3">Outcomes</div>
                                <ul className="space-y-2.5 text-white/80 text-[14px] leading-relaxed">
                                    {outcomes.map((o, i) => (
                                        <li key={i} className="flex gap-2">
                                            <span className="text-[var(--accent-indigo)] font-mono-tech text-[10px] pt-[3px]">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                            <span>{o}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </aside>
                    {/* Right column: body */}
                    <article className="md:col-span-9">
                        {c.body && (
                            <p className="font-serif-display text-2xl md:text-3xl text-white/85 leading-snug mb-10 border-l border-[var(--accent-indigo)] pl-6">
                                {c.body}
                            </p>
                        )}
                        {c.long_body ? (
                            <MarkdownBody source={c.long_body} />
                        ) : null}
                    </article>
                </div>
            </section>

            {next && next !== c && (
                <section className="py-16">
                    <div className="max-w-[1200px] mx-auto px-6 md:px-10">
                        <Reveal>
                            <div className="overline mb-6">Next Case Study</div>
                            <Link to={`/case-studies/${next.slug || slugify(next.title || next.id || "")}`} data-testid="case-next">
                                <TechCard className="!p-8 flex flex-col md:flex-row md:items-center justify-between gap-6 hover:border-[var(--accent-indigo)]/50 transition-colors">
                                    <div>
                                        <div className="chip mb-3">{next.tag}</div>
                                        <h3 className="font-serif-display text-3xl leading-tight">
                                            {next.title}
                                        </h3>
                                    </div>
                                    <ArrowRight size={28} className="text-[var(--accent-indigo)] shrink-0" />
                                </TechCard>
                            </Link>
                        </Reveal>
                    </div>
                </section>
            )}
        </>
    );
}
